import { Component, OnInit } from '@angular/core';
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import {
  CloudAccount,
  CloudCustomer,
  CloudEnvironment,
  CreateDevOpsActionRequest,
  DevOpsActionAndParameter,
  DevOpsActionParameter,
  ErmDevOpsApiClientService
} from 'erm-sdk';
import { CreateDevopsActionBaseComponent } from '../create-devops-action-base.component';
import * as _ from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { AddNewDevOpsActionParameterComponent } from '../../add-new-dev-ops-action-parameter/add-new-dev-ops-action-parameter.component';

@Component({
  standalone: false,
  selector: 'app-create-devops-action',
  templateUrl: './create-devops-action.component.html',
  styleUrls: ['./create-devops-action.component.scss']
})
export class CreateDevopsActionComponent extends CreateDevopsActionBaseComponent implements OnInit {

  isLoading = false;

  customerGridOptions: GridOptions;
  customerGridApi?: GridApi;
  accountGridOptions: GridOptions;
  accountGridApi?: GridApi;
  environmentGridOptions: GridOptions;
  environmentGridApi?: GridApi;

  selectedFetchOption?: string;
  selectedCustomer?: CloudCustomer;
  selectedAccount?: CloudAccount;
  selectedEnvironment?: CloudEnvironment;
  selectedRegion?: string;

  devOpsActionsAndParameters?: DevOpsActionAndParameter[];

  constructor(ermDevOpsApiClient: ErmDevOpsApiClientService, private matdialog: MatDialog) {
    super(ermDevOpsApiClient);

    // Customers Ag-Grid Options
    this.customerGridOptions = {
      columnDefs: this.createCustomerColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedCustomer = _.first(event.api.getSelectedNodes().map(x => x.data));
        if (this.selectedCustomer) {
          this.createDevOpsActionRequest.customerId = this.selectedCustomer.customerId;
        }
        else {
          this.createDevOpsActionRequest.customerId = undefined;
        }
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.customerGridApi = params.api;
      }
    };

    // Accounts Ag-Grid Options
    this.accountGridOptions = {
      columnDefs: this.createAccountColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedAccount = _.first(event.api.getSelectedNodes().map(x => x.data));
        if (this.selectedAccount) {
          this.createDevOpsActionRequest.accountId = this.selectedAccount.accountId;
        }
        else {
          this.createDevOpsActionRequest.accountId = undefined;
        }
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.accountGridApi = params.api;
      }
    };

    // Environment Ag-Grid Options
    this.environmentGridOptions = {
      columnDefs: this.createEnvironmentColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedEnvironment = _.first(event.api.getSelectedNodes().map(x => x.data));
        if (this.selectedEnvironment) {
          this.createDevOpsActionRequest.environmentId = this.selectedEnvironment.environmentId;
        }
        else {
          this.createDevOpsActionRequest.environmentId = undefined;
        }
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.environmentGridApi = params.api;
      }
    };
  }

  async ngOnInit(): Promise<void> {
    try {

      this.isLoading = true;

      // Get DevOps Actions
      const getDevopsActionsAndParameter = await this.ermDevOpsApiClient.getDevOpsActionsAndParameters();
      this.devOpsActionsAndParameters = getDevopsActionsAndParameter.body?.devOpsActionsAndParameters || new Array<DevOpsActionAndParameter>();

      // Get customers
      const getCustomersResponse = await this.ermDevOpsApiClient.getCloudCustomers();
      this.cloudCutomers = getCustomersResponse.body?.cloudCustomers || new Array<CloudCustomer>();

      // Get accounts
      const getAccountsResponse = await this.ermDevOpsApiClient.getCloudAccounts();
      this.cloudAccounts = getAccountsResponse.body?.cloudAccounts || new Array<CloudAccount>();

      // Get environments
      const getEnvironmentsResponse = await this.ermDevOpsApiClient.getCloudEnvironments();
      this.cloudEnvironments = getEnvironmentsResponse.body?.cloudEnvironments || new Array<CloudEnvironment>();

      // Get Supported Regions
      const getSupportedRegions = await this.ermDevOpsApiClient.getSupportedRegions();
      this.supportedRegions = getSupportedRegions.body?.regions || [];

      // Initialize ag-grid
      this.customerGridApi?.setGridOption('rowData', this.cloudCutomers);
      this.accountGridApi?.setGridOption('rowData', this.cloudAccounts);
      this.environmentGridApi?.setGridOption('rowData', this.cloudEnvironments);
    } finally {
      this.isLoading = false;
    }
  }

  private createCustomerColumnDefs(): any[] {
    return [
      {
        headerName: 'Customer Id',
        field: 'customerId',
      },
      { headerName: 'Name', field: 'name' },
      { headerName: 'Description', field: 'description', width: 300 }
    ];
  }

  private createAccountColumnDefs(): any[] {
    return [
      {
        headerName: 'Account Id',
        field: 'accountId',
      },
      { headerName: 'Description', field: 'description', width: 300 },
      {
        headerName: 'Customer Name',
        field: 'customerId',
        cellRenderer: (params: any) => {
          const customer = this.cloudCutomers?.find(x => x.customerId === params.value);
          return `<span>${customer?.name}</span>`;
        },
      },
      { headerName: 'Region', field: 'region' },
      { headerName: 'Maintenance', field: 'inMaintenanceMode' },
      { headerName: 'Enabled', field: 'isEnabled' }
    ];
  }

  private createEnvironmentColumnDefs(): any[] {
    return [
      {
        headerName: 'Environment Id',
        field: 'environmentId',
        width: 140
      },
      { headerName: 'Name', field: 'name' },
      { headerName: 'Description', field: 'description', width: 300 },
      {
        headerName: 'Production',
        field: 'isProduction',
        cellStyle: (params: any) => {
          if (params.value === true) {
            return {
              backgroundColor: 'yellow'
            };
          } else {
            return null;
          }
        },
        width: 140
      },
      {
        headerName: 'Customer Name',
        field: 'customerId',
        cellRenderer: (params: any) => {
          const customer = this.cloudCutomers?.find(x => x.customerId === params.value);
          return `<span>${customer?.name}</span>`;
        },
        width: 300
      },
      {
        headerName: 'Account Description',
        field: 'accountId',
        cellRenderer: (params: any) => {
          const account = this.cloudAccounts?.find(x => x.accountId === params.value);
          return `<span>${account?.description}</span>`;
        },
        width: 300
      },
      { headerName: 'Version', field: 'version' },
      { headerName: 'Region', field: 'region', width: 140 },
      { headerName: 'Schema', field: 'schemaName' },
      { headerName: 'Deployment Type', field: 'deploymentType', width: 180 },
      { headerName: 'Maintenance', field: 'inMaintenanceMode' },
      { headerName: 'Available', field: 'isAvailable' },
      { headerName: 'Enabled', field: 'isEnabled' },
      { headerName: 'Status', field: 'statusMessage' }
    ];
  }

  addNewDevOpsActionParameter(): void {
    // AddNewDevOpsActionParameterComponent
    const addNewDevOpsActionParameterDialogRef = this.matdialog.open(AddNewDevOpsActionParameterComponent);
    addNewDevOpsActionParameterDialogRef.afterClosed().subscribe(async dialogResult => {
      const result = dialogResult;
      if (result && result.data) {
        const newParameter = result.data as DevOpsActionParameter;
        // add new parameter to action parameter list
        if (newParameter && this.selectedActionParameterArray) {
          this.selectedActionParameterArray.push(newParameter);
        }
      }
    });
  }

  okClicked(): void {
    // reset page, allowing user to create another DevOps Action
    // in future improve UI to display success message in dialog box
    this.createDevOpsActionResponse = undefined;
    this.createDevOpsActionRequest = {} as CreateDevOpsActionRequest;
    this.selectedDevOpsActionAndParameter = undefined;
  }
}
